import type { Point2, ViewportState } from '@frontend/viewer-core';

import { SNAP_TOLERANCE_PX } from '../constants';
import type {
  DrawingModel,
  MeasureEdge,
  MeasureModifiers,
  PointerState,
  ToolId,
} from './types';

type Dependencies = {
  viewport: {
    getState(): ViewportState;
    screenToWorld(screen: Point2): Point2;
  };
  getModel: () => DrawingModel | null;
  getTool: () => ToolId;
  getHiddenLayers: () => ReadonlySet<number>;
  getMeasureModifiers: () => MeasureModifiers;
};

/** Resolves a screen point to a world point, snapped when the Measure tool is active. */
export type SnapResolver = (screenPoint: Point2) => PointerState;

/**
 * Which kind of edge wins when several are in snap range, lowest first. A
 * closer edge of a later kind does not override an earlier one.
 */
const EDGE_RANK: Record<MeasureEdge['kind'], number> = {
  line: 0,
  circle: 1,
  curve: 2,
};

/**
 * Builds the resolver shared by the live pointer overlay and the Measure tool.
 *
 * Both need the exact same answer to "where does this screen point land, and
 * onto what" — the overlay to draw a snap indicator, Measure to commit the
 * point it draws. A second implementation of the tolerance or the fallback
 * order would eventually drift from this one.
 */
export const createSnapResolver = ({
  viewport,
  getModel,
  getTool,
  getHiddenLayers,
  getMeasureModifiers,
}: Dependencies): SnapResolver => {
  /**
   * Resolves a screen point to the measure target a click there would pick: a
   * snap in range, then a line, a circle or a spot on any other curve, then a
   * free point. A snap on a line's midpoint picks the line, and one on a
   * CIRCLE's centre picks the circle; any other snap picks a point. The
   * tolerance is a screen distance converted through the current zoom, so
   * snapping feels the same at every magnification.
   */
  return (screenPoint) => {
    const world = viewport.screenToWorld(screenPoint);
    const model = getModel();
    if (!model) {
      return { screen: screenPoint, world, snap: null, target: null };
    }

    // Snapping is a Measure affordance. Skipping it elsewhere also keeps two
    // spatial queries off every pointer move under the Select tool.
    if (getTool() !== 'measure') {
      return { screen: screenPoint, world, snap: null, target: null };
    }

    const freePoint: PointerState = {
      screen: screenPoint,
      world,
      snap: null,
      target: { kind: 'point', spot: world, entityKey: null },
    };
    if (getMeasureModifiers().isFreePoint) {
      return freePoint;
    }

    const { scale } = viewport.getState();
    const tolerance = SNAP_TOLERANCE_PX / (scale || 1);
    const hiddenLayers = getHiddenLayers();

    // Distinctive targets first — a corner, a centre, a midpoint. These are the
    // points a person is actually reaching for, and they must not be beaten by a
    // spot on a line that happens to be a fraction closer.
    const discrete = model.snapIndex.nearest(world, tolerance, hiddenLayers);
    if (discrete) {
      const spot = discrete.point;
      const entityKey = discrete.entityId ?? null;
      return {
        screen: screenPoint,
        world: spot,
        snap: discrete,
        target:
          discrete.measureEdge && entityKey
            ? { ...discrete.measureEdge, spot, entityKey }
            : { kind: 'point', spot, entityKey },
      };
    }

    // Dimension and annotation geometry has no measure edge, so it is never a
    // target, shown or not.
    const onEdge = model.index.nearestPointOn(world, {
      tolerance,
      hiddenLayers,
      rankSegment: (loopId, chunkIndex, segmentIndex) => {
        const edge = model.measureEdgeAt(loopId, chunkIndex, segmentIndex);
        return edge ? EDGE_RANK[edge.kind] : null;
      },
    });
    const edge =
      onEdge &&
      model.measureEdgeAt(
        onEdge.loopId,
        onEdge.chunkIndex,
        onEdge.segmentIndex,
      );
    const chunk =
      onEdge && model.loopsById.get(onEdge.loopId)?.chunks[onEdge.chunkIndex];
    if (!onEdge || !edge || !chunk) {
      return freePoint;
    }

    const spot = onEdge.point;
    return {
      screen: screenPoint,
      world: spot,
      // Lines and circles light up instead; a curve spot is shown as a point.
      snap:
        edge.kind === 'curve'
          ? {
              point: spot,
              kind: 'edge',
              layer: onEdge.layer,
              entityId: chunk.entityId,
            }
          : null,
      target: { ...edge, spot, entityKey: chunk.entityId },
    };
  };
};
